import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import EmptyState from '@/components/ui/EmptyState';
import Lightbox from '@/components/ui/Lightbox';
import SectionHeader from '@/components/ui/SectionHeader';
import CandidatePicker from '@/components/production/CandidatePicker';
import DesignTile from '@/components/production/tiles/DesignTile';
import InstructButton from '@/components/production/tiles/InstructButton';
import { useProductionContext } from '@/context/ProductionContext';
import { useDesignAssets } from '@/hooks/useDesignAssets';
import { VIEW_SUFFIX_RE, bustSrc, candidateParent, normKey, thumbSrc } from '@/lib/assetKeys';

function Group({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-faint">
        {title}
        {hint && <span className="ml-2 font-normal normal-case tracking-normal text-faint/80">{hint}</span>}
      </h4>
      {children}
    </div>
  );
}

/** Characters, locations and hero props — the canonical look the film is built from. */
export default function DesignReferences({ breakdown }: { breakdown?: unknown }) {
  const { t } = useTranslation('production');
  const { assets, productionId, pendingRework, finalAssets, running, reworkBust, vertical } = useProductionContext();
  const { characters, viewsFor, locations, props, plannedLocs, decidedChar, decidedLoc, decidedProp } = useDesignAssets(
    assets,
    breakdown,
  );

  const [enlarged, setEnlarged] = useState<{ src: string; label: string } | null>(null);
  // A post-hoc candidate swap overwrites the canonical file, so bump a counter to
  // force the tile to refetch it.
  const [swapBust, setSwapBust] = useState<Record<string, number>>({});
  const bumpSwap = useCallback((name: string) => {
    setSwapBust((s) => ({ ...s, [normKey(name)]: (s[normKey(name)] || 0) + 1 }));
  }, []);

  if (characters.length === 0 && locations.length === 0 && plannedLocs.length === 0) {
    return (
      <EmptyState title={t('design.emptyTitle')}>
        {t('design.empty')} {t('design.help')}
      </EmptyState>
    );
  }

  const isDecided = (shotId: string, decided: Set<string>) => {
    const parent = candidateParent(shotId);
    return !!parent && decided.has(normKey(parent));
  };

  return (
    <div>
      <SectionHeader
        title={t('step.design')}
        help={t('design.help')}
        subtitle={t('design.summary', {
          characters: t('design.charactersCount', { count: characters.length }),
          locations: t('design.locationsCount', { count: locations.length }),
        })}
      />

      <div className="space-y-6">
        {characters.length > 0 && (
          <Group title={t('design.characters')}>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-3">
              {characters.map((c) => {
                const decided = isDecided(c.shot_id, decidedChar);
                const views = viewsFor(c.shot_id);
                return (
                  <DesignTile
                    key={c.shot_id}
                    label={c.shot_id}
                    src={thumbSrc(bustSrc(c.path, reworkBust[normKey(c.shot_id)]))}
                    onOpen={() => setEnlarged({ src: bustSrc(c.path, reworkBust[normKey(c.shot_id)]), label: c.shot_id })}
                    decided={decided}
                    pendingRework={pendingRework.has(normKey(c.shot_id))}
                    iterating={running && !decided && !finalAssets.has(`character:${normKey(c.shot_id)}`)}
                    actions={<InstructButton productionId={productionId} assetRef={`character:${c.shot_id}`} label={c.shot_id} />}
                  >
                    {views.length > 0 && (
                      <div className="mt-1 flex gap-1 overflow-x-auto">
                        {views.map((v) => (
                          <div
                            key={v.shot_id}
                            className="relative shrink-0 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEnlarged({ src: bustSrc(v.path, reworkBust[normKey(c.shot_id)]), label: v.shot_id });
                            }}
                          >
                            <img
                              src={thumbSrc(bustSrc(v.path, reworkBust[normKey(c.shot_id)]))}
                              alt={v.shot_id}
                              className="h-16 rounded border border-border object-cover"
                            />
                            <span className="absolute bottom-0 left-0 right-0 truncate bg-black/60 px-0.5 text-center text-[8px] text-white">
                              {(VIEW_SUFFIX_RE.exec(v.shot_id)?.[1] || '').toLowerCase()}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </DesignTile>
                );
              })}
            </div>
          </Group>
        )}

        {(locations.length > 0 || plannedLocs.length > 0) && (
          <Group title={t('design.locations')}>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
              {locations.map((loc) => {
                const decided = isDecided(loc.shot_id, decidedLoc);
                const bust = (reworkBust[normKey(loc.shot_id)] || 0) + (swapBust[normKey(loc.shot_id)] || 0) || undefined;
                return (
                  <DesignTile
                    key={loc.shot_id}
                    label={loc.shot_id}
                    badge={loc.location_id}
                    src={thumbSrc(bustSrc(loc.path, bust))}
                    onOpen={() => setEnlarged({ src: bustSrc(loc.path, bust), label: loc.shot_id })}
                    decided={decided}
                    pendingRework={pendingRework.has(normKey(loc.shot_id))}
                    iterating={running && !decided && !finalAssets.has(`location:${normKey(loc.shot_id)}`)}
                    actions={
                      <>
                        <InstructButton productionId={productionId} assetRef={`location:${loc.shot_id}`} label={loc.shot_id} />
                        <CandidatePicker
                          productionId={productionId}
                          assetType="location"
                          assetName={loc.shot_id}
                          onSwapped={bumpSwap}
                          vertical={vertical}
                        />
                      </>
                    }
                  />
                );
              })}
              {plannedLocs.map((name) => (
                <div
                  key={`planned-${name}`}
                  className="relative flex aspect-video flex-col items-center justify-center
                             rounded-lg border border-dashed border-border bg-muted/30"
                >
                  <Loader2 className="h-4 w-4 animate-spin text-faint" />
                  <div className="absolute bottom-0 left-0 right-0 truncate px-1 py-1 text-center text-[10px] text-faint">
                    {name} · {t('state.queued', { ns: 'common' })}
                  </div>
                </div>
              ))}
            </div>
          </Group>
        )}

        {props.length > 0 && (
          <Group title={t('design.props')} hint={t('design.propsHint')}>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
              {props.map((pr) => (
                <DesignTile
                  key={pr.shot_id}
                  label={pr.shot_id}
                  src={thumbSrc(bustSrc(pr.path, swapBust[normKey(pr.shot_id)] || undefined))}
                  onOpen={() =>
                    setEnlarged({ src: bustSrc(pr.path, swapBust[normKey(pr.shot_id)] || undefined), label: pr.shot_id })
                  }
                  decided={isDecided(pr.shot_id, decidedProp)}
                  actions={
                    <>
                      <InstructButton productionId={productionId} assetRef={`prop:${pr.shot_id}`} label={pr.shot_id} />
                      <CandidatePicker
                        productionId={productionId}
                        assetType="prop"
                        assetName={pr.shot_id}
                        onSwapped={bumpSwap}
                        vertical={vertical}
                      />
                    </>
                  }
                />
              ))}
            </div>
          </Group>
        )}
      </div>

      {enlarged && (
        <Lightbox label={enlarged.label} onClose={() => setEnlarged(null)}>
          <img
            src={enlarged.src}
            alt={enlarged.label}
            className="mx-auto max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
          />
        </Lightbox>
      )}
    </div>
  );
}
